/* eslint-disable import/order -- the `react-semantic-ui-datepickers` stylesheet is a
   SIDE-EFFECT import, and the ordering this rule asks for moves module imports across
   it. Stylesheet order is cascade order, so that is a change to what the page renders,
   not to its types. */
import { useState, useEffect } from 'react';
import { Form, Select, Dimmer, Loader, TextArea } from 'semantic-ui-react';
import type { DropdownProps } from 'semantic-ui-react';

import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css';
import { useAuthContext } from '../../../contexts/AuthContext';
import { FHIRProxyClient, FlagBuilder, NoteBuilder, ProvenanceBuilder } from '../../../fhir-sdoh';
import type { Option, PractitionerOption } from '../../../types';


import type { Flag, Provenance } from 'fhir/r4';

/**
 * The in-progress note this form holds before `NoteBuilder.build` turns it into a
 * DocumentReference.
 *
 * `noteTag` carries practitioner *ids* while the adjacent `selectedPractitioner`
 * state carries their display names — the two are appended to in step by
 * `handleNoteTagChange` and are read back separately when the note is saved.
 */
interface NoteDraft {
  noteType: string;
  noteText: string;
  noteTypeCode: string;
  noteTag: string[];
  referralId: string[];
}

/**
 * Transcribed from the producers, `notes/list.tsx:428` and `notes/listAll.tsx:788`,
 * which render this with identical props except `allReferralData` (only `listAll`
 * passes it).
 */
interface CreateNoteProps {
  practitionerData: PractitionerOption[];
  setValidationErrors: React.Dispatch<React.SetStateAction<string[]>>;
  patientId: string;
  token: string;
  type: string;
  refresh: () => void;
  /** One `data/NoteTypes` bucket, selected by `type` at the call site. */
  noteTypes: Option[];
  system: string;
  noteCategories: Record<string, { code: string; display: string }>;
  org: string;
  /** Only `id`/`referralId` are read, to build the referral dropdown. */
  // `| undefined` on both fields: the producer's `ReferralSummary` declares them
  // optional, and `exactOptionalPropertyTypes` will not pass that through a bare
  // `?:`. Same shape as in `listAll.tsx`, which is the only caller that supplies it.
  allReferralData?: { id?: string | undefined; referralId?: string | undefined }[] | undefined;
}

// Parent note creation
function CreateNote(props: CreateNoteProps) {
  const authInfo = useAuthContext();
  const practitionerName = authInfo.practitionerName;
  const practitionerId = authInfo.practitionerId;
  const practitioners = props.practitionerData;
  const [note, setNote] = useState<NoteDraft>({
    noteType: '',
    noteText: '',
    noteTypeCode: '',
    noteTag: [],
    referralId: [], //allow for multiple referral ids for one note
  });

  const [loaderActive, setLoaderActive] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState<string[]>([]);

  useEffect(() => {
    setNote((prevState) => ({
      ...prevState,
      noteType: '',
      noteTypeCode: '',
      noteText: '',
      noteTag: [],
      referralId: [],
    }));
    setSelectedPractitioner([]);

    props.setValidationErrors([]);

     
   
  }, [props.type]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setNote((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  /* `e.target` is the clicked option element; semantic-ui types it only as `EventTarget`. */
  const handleNoteTypeChange = (e: React.SyntheticEvent<HTMLElement> & { target: HTMLElement }, _value: NoteDraft) => {
    props.noteTypes.forEach((note) => {
      if (note.text === e.target.textContent) {
        setNote((prevState) => ({
          ...prevState,
          noteType: e.target.textContent,
          noteTypeCode: note.value,
        }));
      }
    });
  };

  /* `e.target` is the clicked option/delete-icon element; semantic-ui types it only as `EventTarget`. */
  const handleNoteTagChange = (e: React.SyntheticEvent<HTMLElement> & { target: HTMLElement }, _value: NoteDraft) => {
    //to delete the practitioner if delete option is selected
    if (e.target?.className === 'delete icon') {
      setSelectedPractitioner((prevState) =>
        prevState.filter((practitioner) => practitioner !== e.target?.previousSibling?.textContent)
      );
      practitioners.forEach((note) => {
        if (note.text === e.target?.previousSibling?.textContent) {
          setNote((prevState) => ({
            ...prevState,
            noteTag: [...prevState.noteTag.filter((practitionerId) => practitionerId !== note.value)],
          }));
        }
      });
    } else if (e.target?.className === 'dropdown icon clear') {
      setNote((prevState) => ({
        ...prevState,
        noteTag: [],
      }));
      setSelectedPractitioner([]);
    } else {
      practitioners.forEach((note) => {
        if (note.text === e.target.textContent) {
          setSelectedPractitioner((prevState) => [...prevState, e.target.textContent]);
          setNote((prevState) => ({
            ...prevState,
            noteTag: [...prevState.noteTag, note.value],
          }));
        }
      });
    }
  };

  const handleNoteReferralChange = (value: string[]) => {
    setNote((prevState) => ({
      ...prevState,
      referralId: value,
    }));
  };

  const validateNotes = (_e: React.FormEvent<HTMLFormElement>) => {
    let validData = true;
    const validateKeys: { key: keyof NoteDraft; value: string }[] = [
      { key: 'noteType', value: 'Note Type' },
      { key: 'noteText', value: 'Note Text' },
    ];
    props.setValidationErrors([]);
    validateKeys.forEach((validateObj) => {
      if (note[validateObj.key] === '') {
        validData = false;
        props.setValidationErrors((state) => [...state, validateObj.value + ' is a mandatory field']);
      }
    });

    if (validData) {
      // Submit
      createNote(props.patientId, authInfo.token, note);
    }
  };

  // FHIR Call to create a Note
  const createNote = (id: string, token: string, note: NoteDraft) => {
    let tagnotes = '';
    // Assigned only inside the red-flag branch below; the `if (!flagpayload)`
    // further down reads it on every other path.
    let flagpayload: Flag | undefined;
    if (selectedPractitioner?.length > 0) {
      selectedPractitioner?.forEach((practitioner) => (tagnotes += ` #` + practitioner));
      flagpayload = FlagBuilder.build(
        props.patientId,
        authInfo.practitionerId,
        { code: 'admin', display: 'administrative' },
        { code: 'ALERT', display: 'Alert' },
        note.noteText + ' :Tagged you by ' + authInfo.practitionerName,
        authInfo.practitionerName,
        props.org,
        note.noteTag.toString() // assignedTo
      );
    }
    const notePayload = NoteBuilder.build(
      id.toString(),
      selectedPractitioner.length !== 0 ? note.noteText + tagnotes : note.noteText,
      practitionerId,
      props.noteCategories[props.type]!.code,
      props.noteCategories[props.type]!.display,
      note.noteTypeCode.toString(),
      note.noteType.toString(),
      props.system,
      false,
      '',
      practitionerName,
      '',
      note.referralId
    );
    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    //create Provenance
    const provenance: Provenance = ProvenanceBuilder.build(
      `DocumentReference/${notePayload.id!}`,
      {
        reference: 'Practitioner/' + authInfo.practitionerId,
        display: practitionerName,
      }, //eg. {Practitioner/oa2,johnsmith},
      id,
      'CREATE',
      `A ${notePayload?.type?.coding?.[0]?.code} Note (${notePayload?.description}) has been created`,
      { code: props.org.toUpperCase(), display: props.org.toLowerCase() } //eg. {Organization/HUMANA,humana}
    );

    //now post bundle request
    let fhirBundle;
    if (!flagpayload) {
      fhirBundle = ProvenanceBuilder.buildProvenanceBundle([notePayload], provenance);
    } else {
      fhirBundle = ProvenanceBuilder.buildProvenanceBundle([notePayload, flagpayload], provenance);
    }
    setLoaderActive(true);

    /* eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- the value is a `fhir-sdoh` export whose module ships no usable types */
    fhirProxyClient.send('post', '', fhirBundle).then(
      (_response) => {
        setLoaderActive(false);
        // Reset
        setNote({
          noteType: '',
          noteText: '',
          noteTypeCode: '',
          noteTag: [],
          referralId: [],
        });
        setSelectedPractitioner([]);
        props.refresh();
      },
      (error: { toString(): string }) => {
        setLoaderActive(false);
        props.setValidationErrors((state) => [...state, 'Unable to create note: ' + error.toString()]);
      }
    );
  };

  return (
    <div>
      <Dimmer active={loaderActive} inverted>
        <Loader></Loader>
      </Dimmer>

      <div>
        <Form onSubmit={validateNotes} autoComplete="off" error>
          <Form.Group widths="equal">
            <Form.Field
              id="form-select-control-notes-type"
              required
              control={Select}
              options={props.noteTypes}
              selectOnBlur={false}
              placeholder="Note Type"
              name="noteType"
              value={note.noteTypeCode}
              onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLElement }) => handleNoteTypeChange(e, note)}
              search
              searchInput={{ id: 'form-select-control-notes-type' }}
            />
            <Form.Field
              name="referralId"
              value={note.referralId}
              type="text"
              placeholder="Enter Referral ID"
              selectOnBlur={false}
              control={Select}
              clearable
              multiple
              options={
                (props?.allReferralData?.length ?? 0) > 0 &&
                props?.allReferralData?.map((k: { id?: string | undefined; referralId?: string | undefined }) => {
                  return { key: k?.id, value: k?.referralId, text: k?.referralId?.toUpperCase() };
                })
              }
              onChange={(_e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => handleNoteReferralChange(value as string[])}
            />
            <Form.Field
              id="form-select-control-notes-tag"
              clearable
              multiple
              control={Select}
              options={practitioners}
              selectOnBlur={false}
              placeholder="Tag"
              name="noteTag"
              value={note.noteTag}
              onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLElement }) => handleNoteTagChange(e, note)}
              search
              searchInput={{ id: 'form-select-control-notes-tag' }}
            />
          </Form.Group>
          <Form.Group>
            <Form.Input
              id="form-input-control-note-text"
              required
              control={TextArea}
              rows={2}
              width={14}
              placeholder="Note"
              name="noteText"
              value={note.noteText}
              onChange={handleChange}
            />
            <Form.Button width={1} size="medium" compact color="blue" type="submit" style={{ marginTop: '7px' }}>
              Add Note
            </Form.Button>
          </Form.Group>
        </Form>
      </div>
    </div>
  );
}

export default CreateNote;
